import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, ShieldCheck, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const LOGO_URL = "/agha_logo_sm.png";

export default function LoginPage() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      toast.error("الرجاء إدخال اسم المستخدم وكلمة المرور");
      return;
    }
    setBusy(true);
    const res = await login(username.trim(), password);
    setBusy(false);
    if (res.ok) {
      toast.success(`أهلاً ${username}`);
      navigate("/");
    } else {
      toast.error(res.error || "فشل تسجيل الدخول");
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-[#F4F1EA]">
      {/* Left hero (visual) */}
      <div className="hidden lg:flex lg:col-span-3 login-hero items-center justify-center p-12 relative">
        <div className="absolute top-20 right-24 w-72 h-72 rounded-full bg-amber-500/20 blur-3xl float-blob" />
        <div className="absolute bottom-32 left-20 w-96 h-96 rounded-full bg-teal-400/20 blur-3xl float-blob" style={{ animationDelay: "4s" }} />

        <div className="relative z-10 max-w-lg text-white text-center">
          {/* Logo block */}
          <div className="mb-10 inline-block">
            <div className="h-56 w-56 mx-auto rounded-full bg-white shadow-2xl shadow-black/40 overflow-hidden ring-4 ring-amber-400/30 hover:ring-amber-400/60 transition-all">
              <img
                src="/agha_logo_md.png"
                alt="Agha Phone"
                className="h-full w-full object-cover scale-150"
              />
            </div>
          </div>

          {/* Tagline */}
          <h2 className="text-4xl font-display font-bold leading-tight mb-4">
            <span className="font-latin">Agha Phone</span>
          </h2>

          {/* Feature pills */}
          <div className="flex flex-wrap gap-3 justify-center">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 text-sm">
              <ShieldCheck className="h-4 w-4 text-amber-300" />
              <span>محلي وآمن</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 text-sm">
              <Sparkles className="h-4 w-4 text-amber-300" />
              <span>نسخ احتياطية فورية</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 text-sm">
              <Users className="h-4 w-4 text-amber-300" />
              <span>صلاحيات للموظفين</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="lg:col-span-2 flex items-center justify-center px-6 py-12 bg-[#F4F1EA] relative">
        <div className="w-full max-w-md">
          {/* Mobile-only logo */}
          <div className="lg:hidden text-center mb-10">
            <div className="h-44 w-44 mx-auto flex items-center justify-center">
              <img src={LOGO_URL} alt="Agha Phone" className="h-full w-full object-contain drop-shadow-lg" />
            </div>
          </div>

          <div className="mb-8">
            <h1 className="text-4xl font-display font-extrabold text-zinc-900 mb-2 font-latin">Agha Phone</h1>
            <p className="text-zinc-600">سجّل دخولك للوصول إلى لوحة التحكم</p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label htmlFor="username" className="block text-xs font-bold text-zinc-700 uppercase tracking-wider mb-2 font-latin">
                Username · اسم المستخدم
              </label>
              <div className="relative group input-glow-wrap">
                <Input
                  id="username"
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="اكتب اسم المستخدم"
                  className="px-5 py-7 text-base bg-white border-2 border-zinc-200 focus-visible:border-teal-600 focus-visible:ring-0 rounded-xl shadow-sm input-led"
                  autoComplete="username"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-bold text-zinc-700 uppercase tracking-wider mb-2 font-latin">
                Password · كلمة المرور
              </label>
              <div className="relative group input-glow-wrap">
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="px-5 py-7 text-base bg-white border-2 border-zinc-200 focus-visible:border-teal-600 focus-visible:ring-0 rounded-xl shadow-sm input-led"
                  autoComplete="current-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="btn-shine w-full bg-gradient-to-r from-teal-700 to-teal-600 hover:from-teal-800 hover:to-teal-700 text-white py-7 text-base font-bold rounded-xl shadow-lg shadow-teal-600/25 hover:shadow-xl hover:shadow-teal-600/35 hover:-translate-y-0.5"
            >
              {busy ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  جاري الدخول...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  تسجيل الدخول
                  <ArrowLeft className="h-5 w-5" />
                </span>
              )}
            </Button>
          </form>

          <div className="text-center mt-10 text-xs text-zinc-500">
            <div className="font-bold text-zinc-700 mb-1">الدعم الفني</div>
            <div className="font-latin tracking-wider">Mohamed Aldalawe · 07706925289</div>
          </div>
        </div>
      </div>
    </div>
  );
}
